<template>
  <div
    ref="inlineProductPicker"
    class="sp-inline-picker"
    :class="{ 'sp-inline-picker--auto-fill': isAutoFillMode }"
    :style="inlineProductPickerStyle"
    @click.stop
  >
    <div
      v-if="isAutoFillMode"
      class="sp-inline-picker__auto-head"
    >
      <div class="sp-inline-picker__auto-title">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72" />
          <path d="m14 7 3 3" />
          <path d="M5 6v4" />
          <path d="M19 14v4" />
          <path d="M10 2v2" />
          <path d="M7 8H3" />
          <path d="M21 16h-4" />
          <path d="M11 3H9" />
        </svg>
        Auto Fill Display
      </div>
      <div class="sp-inline-picker__auto-subtitle">
        Pick one or more products to populate every shelf.
      </div>
    </div>

    <div
      class="sp-inline-picker__source-tabs"
    >
      <button
        v-for="source in productSources"
        :key="source.value"
        type="button"
        class="sp-inline-picker__source-tab"
        :class="{ active: currentProductSource === source.value }"
        :disabled="source.disabled"
        @click="switchProductSource(source.value)"
      >
        {{ source.label }}
      </button>
    </div>

    <div class="sp-inline-picker__search">
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
      >
        <circle
          cx="5.8"
          cy="5.8"
          r="4.3"
          stroke="currentColor"
          stroke-width="1.4"
        />
        <path
          d="M9 9l3 3"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
        />
      </svg>
      <input
        ref="inlineProductSearchInput"
        v-model="inlineProductSearch"
        class="sp-inline-picker__input"
        :placeholder="searchPlaceholder"
        @input="refreshInlineProductSearch"
        @keydown.esc="closeInlineProductPicker"
      >
    </div>

    <div class="sp-inline-picker__list">
      <template v-if="!isAutoFillMode && !inlineProductPickerLoading && hasInlineProductRecents && inlineProductItems.length && currentProductSource === productSourceTypes.REGULAR">
        <div class="sp-inline-picker__section-heading">
          <span class="sp-inline-picker__section-title">Recent</span>
          <button
            type="button"
            class="sp-inline-picker__clear-recents"
            :disabled="inlineProductPickerSaving"
            @click.stop="clearInlineProductRecents"
          >
            Clear
          </button>
        </div>
        <button
          v-for="item in visibleInlineProductRecentItems"
          :key="`recent-${productItemKey(item)}`"
          type="button"
          class="sp-inline-picker__item"
          :disabled="inlineProductPickerSaving"
          @click="selectInlineProduct(item, { fromRecent: true })"
        >
          <span class="sp-inline-picker__thumb">
            <img
              v-if="productItemThumbnail(item)"
              :src="productItemThumbnail(item)"
              :alt="productItemName(item)"
            >
            <span
              v-else
              class="sp-inline-picker__thumb-placeholder"
            />
          </span>

          <span class="sp-inline-picker__copy">
            <span class="sp-inline-picker__name">{{ productItemName(item) }}</span>
            <span class="sp-inline-picker__code">{{ productItemCode(item) }}</span>
          </span>
        </button>
      </template>
      <div
        v-if="currentProductSource === productSourceTypes.UPLOADS"
        class="px-1 pt-1"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <div 
          class="px-2 py-2 text-center custom-drag-drop-area"
          :class="{ 'custom-drag-drop-area--active': isDraggingOver }"
          @click="$refs.fileInput.click()"
        >
          <div class="pointer-events-none">
            <i class="bi bi-upload mr-1" />Drop images or click to upload
          </div>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          class="product-picker__file-input"
          @change="onFileSelected"
        >
      </div>
      <template v-if="!inlineProductPickerLoading && inlineProductItems.length">
        <div class="sp-inline-picker__section-title sp-inline-picker__section-title--all">
          All products
        </div>
        <div
          v-for="item in inlineProductItems"
          :key="productItemKey(item)"
          class="sp-inline-picker__item"
          :class="{
            'sp-inline-picker__item--selectable': isAutoFillMode,
            selected: isAutoFillMode && isAutoFillProductSelected(item),
          }"
          role="button"
          tabindex="0"
          @click="handleProductItemClick(item)"
          @keydown.enter.prevent="handleProductItemClick(item)"
        >
          <input
            v-if="isAutoFillMode"
            type="checkbox"
            class="sp-inline-picker__checkbox"
            :checked="isAutoFillProductSelected(item)"
            :disabled="inlineProductPickerSaving"
            @click.stop
            @change.stop="toggleAutoFillProduct(item)"
          >

          <span class="sp-inline-picker__thumb">
            <img
              v-if="productItemThumbnail(item)"
              :src="productItemThumbnail(item)"
              :alt="productItemName(item)"
            >
            <span
              v-else
              class="sp-inline-picker__thumb-placeholder"
            />
          </span>

          <span class="sp-inline-picker__copy">
            <span class="sp-inline-picker__name">
              {{ productItemName(item) }}
            </span>

            <span class="sp-inline-picker__code">
              {{ productItemCode(item) }}
            </span>
          </span>
        </div>
      </template>

      <div
        v-if="inlineProductPickerLoading"
        class="sp-inline-picker__state"
      >
        Loading products...
      </div>

      <div
        v-else-if="!inlineProductItems.length"
        class="sp-inline-picker__state"
      >
        No products found
      </div>

      <div
        v-if="inlineProductPickerError"
        class="sp-inline-picker__state sp-inline-picker__state--error"
      >
        {{ inlineProductPickerError }}
      </div>
    </div>
    <div
      class="sp-inline-picker__footer"
    >
      <button
        type="button"
        class="sp-inline-picker__pager-btn"
        :disabled="inlineProductPickerLoading || inlineProductCurrentPage <= 1"
        aria-label="First page"
        @click="goToInlineProductPage(1)"
      >
        &laquo;
      </button>
      <button
        type="button"
        class="sp-inline-picker__pager-btn"
        :disabled="inlineProductPickerLoading || inlineProductCurrentPage <= 1"
        aria-label="Previous page"
        @click="goToInlineProductPage(inlineProductCurrentPage - 1)"
      >
        &lsaquo;
      </button>

      <template v-for="(page, index) in inlineProductPagerPages">
        <span
          v-if="page === 'ellipsis'"
          :key="`ellipsis-${index}`"
          class="sp-inline-picker__pager-ellipsis"
        >
          ...
        </span>
        <button
          v-else
          :key="`page-${page}`"
          type="button"
          class="sp-inline-picker__pager-btn sp-inline-picker__pager-btn--page"
          :class="{ active: page === inlineProductCurrentPage }"
          :disabled="inlineProductPickerLoading"
          @click="goToInlineProductPage(page)"
        >
          {{ page }}
        </button>
      </template>
      <button
        type="button"
        class="sp-inline-picker__pager-btn"
        :disabled="inlineProductPickerLoading || inlineProductCurrentPage >= inlineProductLastPage"
        aria-label="Next page"
        @click="goToInlineProductPage(inlineProductCurrentPage + 1)"
      >
        &rsaquo;
      </button>
      <button
        type="button"
        class="sp-inline-picker__pager-btn"
        :disabled="inlineProductPickerLoading || inlineProductCurrentPage >= inlineProductLastPage"
        aria-label="Last page"
        @click="goToInlineProductPage(inlineProductLastPage)"
      >
        &raquo;
      </button>
    </div>
    <div
      v-if="isAutoFillMode"
      class="sp-inline-picker__auto-footer"
    >
      <div
        v-if="shouldShowAutoFillModePicker"
        class="sp-inline-picker__fill-mode"
      >
        <button
          type="button"
          class="sp-inline-picker__fill-mode-btn"
          :class="{ active: autoFillMode === 'replace-all' }"
          :disabled="inlineProductPickerSaving"
          @click="autoFillMode = 'replace-all'"
        >
          Replace all
        </button>
        <button
          type="button"
          class="sp-inline-picker__fill-mode-btn"
          :class="{ active: autoFillMode === 'empty-only' }"
          :disabled="inlineProductPickerSaving"
          @click="autoFillMode = 'empty-only'"
        >
          Empty shelves only
        </button>
      </div>
      <div class="sp-inline-picker__auto-actions">
        <div class="d-flex align-items-center">
          <span class="sp-inline-picker__selected-count mr-2">{{ autoFillSelectedItems.length }} selected</span>
          <button
            type="button"
            class="sp-inline-picker__clear-recents"
            style="font-size: 11px;"
            @click.stop="clearAutoFillSelectedItems"
          >
            Clear
          </button>
        </div>
        <button
          type="button"
          class="sp-inline-picker__submit"
          :disabled="inlineProductPickerSaving || !autoFillSelectedItems.length"
          @click="submitAutoFill"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72" />
            <path d="m14 7 3 3" />
            <path d="M5 6v4" />
            <path d="M19 14v4" />
            <path d="M10 2v2" />
            <path d="M7 8H3" />
            <path d="M21 16h-4" />
            <path d="M11 3H9" />
          </svg>
          Auto Fill
        </button>
      </div>
    </div>
  </div>
</template>
<script>
const INLINE_PRODUCT_RECENTS_LIMIT = 3;
const INLINE_PRODUCT_RECENTS_STORAGE_KEY = 'displayBuilderInlineProductRecents';
const EXTERNAL_SOURCES = {
  SALSIFY: 'salsify',
};
const PRODUCT_SOURCE_TYPES = {
  REGULAR: 'regular',
  SALSIFY: 'salsify',
  BYNDER: 'bynder',
  UPLOADS: 'uploads',
}

const PRODUCTS_DEFAULT_FOLDER_NAME = 'Display Builder Products';
const DISPLAY_BUILDER_UPLOAD_PRODUCT_TYPE = 'display-builder-upload';

export default {
  name: 'InlineProductBrowser',
  props: {
    inlineProductPickerStyle: {
      type: Object,
      default: () => ({}),
    },
    selectedInstance: {
      type: Object,
      default: null,
    },
    variant: {
      type: String,
      default: 'inline',
    },
    hasExistingShelfProducts: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    inlineProductSearch: '',
    inlineProductSearchTimer: null,
    inlineProductPickerSaving: false,
    inlineProductPickerError: '',
    inlineProductPickerInstanceId: null,
    inlineProductRecentItems: [],
    autoFillSelectedItems: [],
    autoFillMode: 'replace-all',
    currentProductSource: 'regular',
    isSalsifyAvailable: false,
    items: [],
    searchKey: '',
    selectedIds: [],
    selectedItems: [],
    currentPage: 1,
    totalRows: 0,
    pageSize: 8,
    isLoading: false,
    searchTimer: null,
    previewItem: null,
    folderId: null,
    parentFolderId: null,
    uploadedProducts: [],
    isDraggingOver: false,
    dragDepth: 0,
  }),
  computed: {
    isAutoFillMode() {
      return this.variant === 'auto-fill';
    },
    shouldShowAutoFillModePicker() {
      return this.isAutoFillMode && this.hasExistingShelfProducts;
    },
    effectiveAutoFillMode() {
      return this.hasExistingShelfProducts ? this.autoFillMode : 'empty-only';
    },
    searchPlaceholder() {
      if (this.currentProductSource === PRODUCT_SOURCE_TYPES.SALSIFY) {
        return 'Search Salsify...';
      }

      if (this.currentProductSource === PRODUCT_SOURCE_TYPES.UPLOADS) {
        return 'Search uploads...';
      }

      return 'Search catalog...';
    },
    inlineProductItems() {
      if (this.currentProductSource === PRODUCT_SOURCE_TYPES.SALSIFY) {
        return (this.items || [])
          .map((item) => this.normalizeSalsifyProductItem(item))
          .filter(Boolean);
      }

      if (this.currentProductSource === PRODUCT_SOURCE_TYPES.UPLOADS) {
        const uploadedProducts = this.getUniqueUploadedProductItems(
          (this.uploadedProducts || [])
            .map((item) => this.normalizeUploadedProductItem(item))
            .filter((item) => {
              return item
                && item.type !== 'folder'
                && (item.full_url || item.thumbnail || item.thumbnail_path || item.path);
            })
        );
        const query = String(this.inlineProductSearch || '')
          .trim()
          .toLowerCase();

        if (!query) {
          return uploadedProducts;
        }

        return uploadedProducts.filter((item) => {
          return [
            this.productItemName(item),
            this.productItemCode(item),
          ].some((value) => {
            return String(value || '').toLowerCase().includes(query);
          });
        });
      }

      const items =
        this.$store.getters['fileBrowserProductSelector/items'] || [];

      return items
        .filter((item) => {
          return (
            item &&
            item.type !== 'folder' &&
            (item.full_url || item.thumbnail || item.path)
          );
        })
        .slice(0, 8);
    },
    visibleInlineProductRecentItems() {
      if (String(this.inlineProductSearch || '').trim()) {
        return [];
      }

      return this.inlineProductRecentItems
        .filter((item) => this.isCatalogRecentProductItem(item))
        .slice(0, INLINE_PRODUCT_RECENTS_LIMIT);
    },
    hasInlineProductRecents() {
      return this.visibleInlineProductRecentItems.length > 0;
    },
    inlineProductPickerLoading() {
      if (this.currentProductSource === PRODUCT_SOURCE_TYPES.SALSIFY) {
        return this.isLoading;
      }

      if (this.currentProductSource === PRODUCT_SOURCE_TYPES.UPLOADS) {
        return false;
      }

      return Boolean(this.$store.getters['fileBrowserProductSelector/isListLoading']);
    },
    inlineProductCurrentPage() {
      if (this.currentProductSource === PRODUCT_SOURCE_TYPES.SALSIFY) {
        return Number(this.currentPage || 1);
      }

      if (this.currentProductSource === PRODUCT_SOURCE_TYPES.UPLOADS) {
        return 1;
      }

      return Number(this.$store.getters['fileBrowserProductSelector/currentPage'] || 1);
    },
    inlineProductLastPage() {
      if (this.currentProductSource === PRODUCT_SOURCE_TYPES.SALSIFY) {
        return Math.max(1, Math.ceil(Number(this.totalRows || 0) / this.pageSize));
      }

      if (this.currentProductSource === PRODUCT_SOURCE_TYPES.UPLOADS) {
        return 1;
      }

      return Math.max(1, Number(this.$store.getters['fileBrowserProductSelector/lastPage'] || 1));
    },
    inlineProductPagerPages() {
      const current = this.inlineProductCurrentPage;
      const last = this.inlineProductLastPage;

      if (this.isAutoFillMode) {
        if (last <= 3) {
          return Array.from({ length: last }, (_, index) => index + 1);
        }

        if (current <= 3) {
          return [1, 2, 3, 'ellipsis'];
        }

        if (current >= last - 2) {
          return ['ellipsis', last - 2, last - 1, last];
        }

        return ['ellipsis', current - 1, current, current + 1, 'ellipsis'];
      }

      if (last <= 5) {
        return Array.from({ length: last }, (_, index) => index + 1);
      }

      if (current <= 4) {
        return [1, 2, 3, 4, 'ellipsis'];
      }

      if (current >= last - 3) {
        return ['ellipsis', last - 3, last - 2, last - 1, last];
      }

      return ['ellipsis', current - 1, current, current + 1, 'ellipsis'];
    },
    productSourceTypes() {
      return PRODUCT_SOURCE_TYPES;
    },
    productSources() {
      return [
        {
          label: 'Catalog',
          value: PRODUCT_SOURCE_TYPES.REGULAR,
          disabled: false,
        },
        {
          label: 'Uploads',
          value: PRODUCT_SOURCE_TYPES.UPLOADS,
          disabled: false,
        },
        {
          label: 'Salsify',
          value: PRODUCT_SOURCE_TYPES.SALSIFY,
          disabled: !this.isSalsifyAvailable,
        },
        {
          label: 'Bynder',
          value: PRODUCT_SOURCE_TYPES.BYNDER,
          disabled: true,
        },
      ];
    },
  },
  watch: {
    hasExistingShelfProducts(hasProducts) {
      if (this.isAutoFillMode && !hasProducts) {
        this.autoFillMode = 'empty-only';
      }
    },
  },
  async mounted() {
    this.isSalsifyAvailable =
    await this.isExternalSourceAvailable(
      EXTERNAL_SOURCES.SALSIFY
    );
    this.handleProductsFolder();
  },
  beforeDestroy() {
    clearTimeout(this.inlineProductSearchTimer);
  },
  methods: {
    open() {
      this.inlineProductPickerError = '';
      this.inlineProductPickerSaving = false;
      this.resetInlineProductSearch();
      this.inlineProductRecentItems = this.loadInlineProductRecents();

      if (this.isAutoFillMode) {
        this.autoFillSelectedItems = [];
        this.autoFillMode = this.hasExistingShelfProducts ? 'replace-all' : 'empty-only';
      }

      this.$nextTick(this.focusInlineProductSearch);
    },
    setError(message = '') {
      this.inlineProductPickerError = message;
    },
    setPickerInstanceId(instanceId) {
      this.inlineProductPickerInstanceId = instanceId;
    },
    focusInlineProductSearch() {
      const input = this.$refs.inlineProductSearchInput;
      const element = Array.isArray(input) ? input[0] : input;

      element?.focus?.();
      element?.select?.();
    },
    resetInlineProductSearch() {
      clearTimeout(this.inlineProductSearchTimer);
      this.inlineProductSearch = '';
      this.searchKey = '';

      return this.$store.dispatch(
        'fileBrowserProductSelector/setSearchQuery',
        ''
      );
    },
    closeInlineProductPicker(clearPendingTarget = true) {
      this.resetInlineProductSearch();
      this.inlineProductPickerError = '';
      this.$emit('close', {
        clearPendingTarget: clearPendingTarget && !this.inlineProductPickerSaving,
      });
    },
    refreshInlineProductSearch() {
      clearTimeout(this.inlineProductSearchTimer);

      this.inlineProductSearchTimer = setTimeout(async () => {
        if (
          this.currentProductSource === PRODUCT_SOURCE_TYPES.UPLOADS
        ) {
          // inlineProductItems filters uploadedProducts locally.
          return;
        }

        if (
          this.currentProductSource === PRODUCT_SOURCE_TYPES.SALSIFY
        ) {
          this.searchKey = this.inlineProductSearch;
          await this.fetchItems(1);
          return;
        }

        await this.$store.dispatch(
          'fileBrowserProductSelector/setSearchQuery',
          this.inlineProductSearch
        );

        await this.$store.dispatch(
          'fileBrowserProductSelector/fetchCurrentBrowserData'
        );
      }, 250);
    },
    async goToInlineProductPage(page) {
      const nextPage = Math.min(
        this.inlineProductLastPage,
        Math.max(1, Number(page || 1))
      );

      if (nextPage === this.inlineProductCurrentPage || this.inlineProductPickerLoading) {
        return;
      }

      if (this.currentProductSource === PRODUCT_SOURCE_TYPES.SALSIFY) {
        await this.fetchItems(nextPage);
        this.$nextTick(this.focusInlineProductSearch);
        return;
      }

      if (this.currentProductSource === PRODUCT_SOURCE_TYPES.UPLOADS) {
        this.$nextTick(this.focusInlineProductSearch);
        return;
      }

      await this.$store.dispatch('fileBrowserProductSelector/setCurrentPage', nextPage);
      await this.$store.dispatch('fileBrowserProductSelector/fetchCurrentBrowserData');
      this.$nextTick(this.focusInlineProductSearch);
    },
    async switchProductSource(source) {
      if (
        this.inlineProductPickerSaving ||
        source === this.currentProductSource
      ) {
        return;
      }

      this.currentProductSource = source;
      this.inlineProductSearch = '';
      this.inlineProductPickerError = '';

      clearTimeout(this.inlineProductSearchTimer);

      switch (source) {
        case PRODUCT_SOURCE_TYPES.SALSIFY:
          this.searchKey = '';
          await this.fetchItems(1);
          break;

        case PRODUCT_SOURCE_TYPES.UPLOADS:
          if (!this.folderId) {
            await this.handleProductsFolder();
          } else {
            await this.handleFolderContent(this.folderId);
          }
          break;

        case PRODUCT_SOURCE_TYPES.REGULAR:
          await this.$store.dispatch(
            'fileBrowserProductSelector/enableBrowserMode'
          );
          break;

        case PRODUCT_SOURCE_TYPES.BYNDER:
          // Load Bynder products when implemented.
          break;
      }
    },
    isAutoFillProductSelected(item) {
      const productItem = this.normalizePickerProductItem(item);
      const key = String(this.productItemKey(productItem));

      return this.autoFillSelectedItems.some((selectedItem) => {
        return String(this.productItemKey(selectedItem)) === key;
      });
    },
    toggleAutoFillProduct(item) {
      const productItem = this.normalizePickerProductItem(item);
      const key = String(this.productItemKey(productItem));

      if (this.isAutoFillProductSelected(productItem)) {
        this.autoFillSelectedItems = this.autoFillSelectedItems.filter((selectedItem) => {
          return String(this.productItemKey(selectedItem)) !== key;
        });
        return;
      }

      this.autoFillSelectedItems = [
        ...this.autoFillSelectedItems,
        this.normalizeInlineProductRecentItem(productItem),
      ].filter(Boolean);
    },
    submitAutoFill() {
      if (!this.autoFillSelectedItems.length || this.inlineProductPickerSaving) {
        return;
      }

      this.inlineProductPickerSaving = true;
      this.inlineProductPickerError = '';
      this.resetInlineProductSearch();
      this.$emit('auto-fill', {
        mode: this.effectiveAutoFillMode,
        items: this.autoFillSelectedItems,
      });
    },
    finishAutoFill() {
      this.inlineProductPickerSaving = false;
    },
    async selectInlineProduct(item, options = {}) {
      if (!item || this.inlineProductPickerSaving) {
        return;
      }

      this.inlineProductPickerSaving = true;
      this.inlineProductPickerError = '';
      this.resetInlineProductSearch();

      try {
        const selectedInstance = this.getSelectedInstance();

        if (!selectedInstance?.canvas || !selectedInstance?.id) {
          throw new Error('Canvas is still loading');
        }

        let productItem = this.normalizePickerProductItem(item);

        if (options.fromRecent) {
          productItem = await this.pickInlineRecentProduct(productItem);
        } else {
          let pickedProductItem = {
            ...productItem,
            count: Number(productItem.count || 0) + 1,
          };

          if (
            productItem.product_type === DISPLAY_BUILDER_UPLOAD_PRODUCT_TYPE
            || productItem.product_type === 'salsify'
          ) {
            const currentPickedItem = productItem.product_type === DISPLAY_BUILDER_UPLOAD_PRODUCT_TYPE
              ? this.getPickedUploadProductByFileId(productItem.fileId)
              : this.getPickedInlineProductByFileId(productItem.fileId);
            const nextCount = Number(currentPickedItem?.count ?? productItem.count ?? 0) + 1;

            pickedProductItem = currentPickedItem
              ? {
                ...currentPickedItem,
                ...productItem,
                id: currentPickedItem.id,
                count: nextCount,
              }
              : {
                ...productItem,
                count: nextCount,
              };
          }

          await this.$store.dispatch('fileBrowserProductSelector/pickItem', pickedProductItem);
        }

        window.displayBuilderHelper?.setPendingShelfProductIdentity?.({
          fileId: productItem.fileId,
          fileName: productItem.fileName,
          name: productItem.name,
          source: productItem.product_type || this.currentProductSource,
        });

        this.rememberInlineProductRecent(productItem);
        this.rememberDisplayBuilderProductMetadata(productItem);

        selectedInstance.canvas.fire('product-images-selector:add-display-builder-image', {
          instanceId: this.inlineProductPickerInstanceId || selectedInstance.id,
          product: {
            fileId: productItem.fileId,
            fileName: productItem.fileName,
            name: productItem.name,
            path: productItem.path,
            original_path: productItem.original_path,
            full_url: productItem.full_url,
            thumbnail: productItem.thumbnail,
            product_type: productItem.product_type || this.currentProductSource,
          },
        });
        this.closeInlineProductPicker(false);
      } catch (error) {
        console.warn(error);
        this.inlineProductPickerError = 'Could not add product';
      } finally {
        this.inlineProductPickerSaving = false;
      }
    },
    getSelectedInstance() {
      if (this.selectedInstance?.canvas && this.selectedInstance?.id) {
        return this.selectedInstance;
      }

      const canvas = window.displayBuilderHelper?.canvas
        || window.displayBuilderFabricHelper?.canvas
        || null;

      if (!canvas) {
        return this.selectedInstance;
      }

      return {
        id: canvas.instance_id
          || canvas.instanceId
          || window.displayBuilderInstanceId
          || this.selectedInstance?.id
          || null,
        canvas,
      };
    },
    productItemKey(item) {
      return item.id || item.fileId || item.name || item.path;
    },
    getUniqueUploadedProductItems(items) {
      const seenKeys = new Set();

      return items.filter((item) => {
        const key = String(this.productItemKey(item));

        if (seenKeys.has(key)) {
          return false;
        }

        seenKeys.add(key);
        return true;
      });
    },
    stripProductExtension(value) {
      return String(value || '').replace(/\.[^/.]+$/, '');
    },
    isTechnicalProductName(value) {
      const name = String(value || '').trim();

      return !name
        || name.startsWith('salsify:')
        || name.startsWith('asin:')
        || name.startsWith('bynder:');
    },
    getReadableProductName(item) {
      const candidates = [
        item?.product_name,
        item?.standart_title,
        item?.standard_title,
        item?.title,
        item?.label,
        item?.name,
      ];

      return candidates.find((candidate) => {
        return candidate && !this.isTechnicalProductName(candidate);
      }) || '';
    },
    getProductMetadataFileIdCandidates(item) {
      if (item?.product_type === 'salsify') {
        return [
          item?.fileId,
          item?.id ? `salsify:${item.id}` : '',
          item?.asset_id ? `salsify:${item.asset_id}` : '',
          item?.salsify_id ? `salsify:${item.salsify_id}` : '',
        ].filter(Boolean);
      }

      return [
        item?.fileId,
        item?.name,
        item?.fileName,
        item?.id,
      ].filter(Boolean);
    },
    rememberDisplayBuilderProductMetadata(item) {
      if (!window.isDisplayBuilder) {
        return;
      }

      const productName = this.getReadableProductName(item);

      if (!productName) {
        return;
      }

      const metadataByFileId = window.displayBuilderProductMetadataByFileId || {};

      this.getProductMetadataFileIdCandidates(item).forEach((fileId) => {
        metadataByFileId[this.stripProductExtension(fileId)] = {
          product_name: productName,
          title: productName,
          name: productName,
        };
      });

      window.displayBuilderProductMetadataByFileId = metadataByFileId;
    },
    normalizePickerProductItem(item) {
      if (
        this.currentProductSource === PRODUCT_SOURCE_TYPES.SALSIFY
        || item?.product_type === 'salsify'
        || String(item?.fileId || '').startsWith('salsify:')
      ) {
        return this.normalizeSalsifyProductItem(item) || item;
      }

      if (
        this.currentProductSource === PRODUCT_SOURCE_TYPES.UPLOADS
        || item?.product_type === DISPLAY_BUILDER_UPLOAD_PRODUCT_TYPE
      ) {
        return this.normalizeUploadedProductItem(item) || item;
      }

      return item;
    },
    normalizeSalsifyProductItem(item) {
      if (!item) {
        return null;
      }

      const id = item.id || item.asset_id || item.salsify_id || item.product_id || item.fileId;

      if (!id) {
        return null;
      }

      const fileId = String(item.fileId || '').startsWith('salsify:')
        ? item.fileId
        : `salsify:${id}`;
      const path = item.original_path || item.path || item.full_url || item.thumbnail || '';
      const name = item.product_name || item.title || item.name || item.product_id || item.salsify_id || id;

      return {
        ...item,
        id,
        fileId,
        name,
        product_name: item.product_name || item.title || item.name || name,
        title: item.title || item.product_name || item.name || name,
        fileName: item.fileName || name,
        path,
        original_path: item.original_path || path,
        thumbnail: item.thumbnail || item.thumbnail_path || path,
        thumbnail_path: item.thumbnail_path || item.thumbnail || path,
        full_url: item.full_url || path,
        product_type: 'salsify',
        asset_type: item.asset_type || 'salsify',
        type: item.type || 'image',
        count: Number(item.count || 0),
      };
    },
    normalizeUploadedProductItem(item) {
      if (!item) {
        return null;
      }

      const fileName = item.fileName || item.name || '';
      const fileId = this.stripProductExtension(item.fileId || fileName || item.id);
      const path = item.path || item.original_path || this.getShareFilePath(item.full_url) || '';
      const thumbnail = item.thumbnail_path || item.thumbnail || item.url || '';
      const id = item.id || fileId || path || item.full_url;

      if (!id || !fileId) {
        return null;
      }

      const name = fileName || item.product_name || item.title || fileId;

      return {
        ...item,
        id,
        fileId,
        name,
        product_name: item.product_name || item.title || name,
        title: item.title || item.product_name || name,
        fileName: name,
        path,
        original_path: item.original_path || path,
        thumbnail,
        thumbnail_path: item.thumbnail_path || thumbnail,
        full_url: item.full_url || (path ? `/share?file=${path}` : ''),
        product_type: DISPLAY_BUILDER_UPLOAD_PRODUCT_TYPE,
        type: item.type || 'image',
        count: Number(item.count || 0),
      };
    },
    getShareFilePath(url) {
      if (!url) {
        return '';
      }

      try {
        const value = String(url);
        const parsedUrl = new URL(value, window.location.origin);

        return parsedUrl.searchParams.get('file') || value;
      } catch (error) {
        return String(url || '');
      }
    },
    getUploadedProductsByFileId() {
      const productsByFileId = new Map();

      (this.uploadedProducts || []).forEach((item) => {
        const uploadedItem = this.normalizeUploadedProductItem(item);

        if (!uploadedItem?.fileId) {
          return;
        }

        productsByFileId.set(String(uploadedItem.fileId), uploadedItem);
      });

      return productsByFileId;
    },
    getDisplayBuilderShelfProducts() {
      const state = window.displayBuilderState || window.displayBuilderPanel?.state || {};
      const products = [];

      Object.values(state.fixtures || {}).forEach((fixture) => {
        (fixture?.shelves || []).forEach((shelf) => {
          products.push(...(shelf?.products || []));
        });
      });

      return products;
    },
    normalizeUploadFileIdCandidate(value) {
      if (!value) {
        return '';
      }

      const filePath = this.getShareFilePath(value);
      const fileName = String(filePath || value)
        .split('?')[0]
        .split('#')[0]
        .split(/[\\/]/)
        .pop();

      return this.stripProductExtension(fileName || value);
    },
    getDisplayBuilderProductUploadFileId(product) {
      const uploadedProductsByFileId = this.getUploadedProductsByFileId();
      const candidates = [
        product?.fileName,
        product?.name,
        product?.url,
        product?.image,
        product?.thumbnail,
      ];

      return candidates
        .map((value) => this.normalizeUploadFileIdCandidate(value))
        .find((fileId) => uploadedProductsByFileId.has(fileId)) || '';
    },
    async restorePickedUploadProductsFromDisplayBuilderState() {
      const uploadedProductsByFileId = this.getUploadedProductsByFileId();

      if (!uploadedProductsByFileId.size) {
        return;
      }

      const pickedItems = this.$store.getters['fileBrowserProductSelector/pickedItems'] || [];
      const pickedFileIds = new Set(
        pickedItems
          .map((item) => this.normalizeUploadFileIdCandidate(
            item?.fileId || item?.fileName || item?.name
          ))
          .filter(Boolean)
      );
      const restoreItemsByFileId = new Map();

      this.getDisplayBuilderShelfProducts().forEach((product) => {
        const fileId = this.getDisplayBuilderProductUploadFileId(product);
        const uploadedItem = uploadedProductsByFileId.get(fileId);

        if (!uploadedItem || pickedFileIds.has(fileId)) {
          return;
        }

        const restoreItem = restoreItemsByFileId.get(fileId);

        if (restoreItem) {
          restoreItem.count += 1;
          return;
        }

        restoreItemsByFileId.set(fileId, {
          ...uploadedItem,
          id: uploadedItem.id || fileId,
          fileId,
          count: 1,
        });
      });

      for (const item of restoreItemsByFileId.values()) {
        await this.$store.dispatch('fileBrowserProductSelector/pickItem', item);
      }
    },
    getPickedInlineProductByFileId(fileId) {
      return (this.$store.getters['fileBrowserProductSelector/pickedItems'] || [])
        .find((pickedItem) => String(pickedItem.fileId || '') === String(fileId || ''));
    },
    getPickedUploadProductByFileId(fileId) {
      const normalizedFileId = this.normalizeUploadFileIdCandidate(fileId);

      return (this.$store.getters['fileBrowserProductSelector/pickedItems'] || [])
        .find((pickedItem) => {
          const pickedFileId = this.normalizeUploadFileIdCandidate(
            pickedItem?.fileId || pickedItem?.fileName || pickedItem?.name
          );

          return pickedFileId === normalizedFileId;
        });
    },
    getInlineProductRecentFileIdCandidates(item) {
      const values = [
        item.fileId,
        this.stripProductExtension(item.name),
        this.stripProductExtension(item.fileName),
      ];

      if (item.product_type === 'salsify') {
        values.unshift(String(item.fileId || '').startsWith('salsify:')
          ? item.fileId
          : `salsify:${item.id}`);
      }

      if (item.product_type === DISPLAY_BUILDER_UPLOAD_PRODUCT_TYPE) {
        values.unshift(item.fileId);
      }

      if (String(item.fileId || '').startsWith('asin:')) {
        values.unshift(item.fileId);
      }

      return [...new Set(
        values
          .filter(Boolean)
          .map((value) => String(value).trim())
          .filter(Boolean)
      )];
    },
    async pickInlineRecentProduct(item) {
      const recentItem = this.normalizeInlineProductRecentItem(item);

      if (!recentItem) {
        throw new Error('Recent product is missing selector data.');
      }

      clearTimeout(this.inlineProductSearchTimer);

      for (const fileId of this.getInlineProductRecentFileIdCandidates(recentItem)) {
        const isUploadedProduct = recentItem.product_type === DISPLAY_BUILDER_UPLOAD_PRODUCT_TYPE;
        const currentPickedItem = isUploadedProduct
          ? this.getPickedUploadProductByFileId(fileId)
          : this.getPickedInlineProductByFileId(fileId);
        const nextCount = Number(currentPickedItem?.count || 0) + 1;

        if (currentPickedItem) {
          const pickedProductItem = {
            ...currentPickedItem,
            ...(isUploadedProduct ? recentItem : {}),
            id: currentPickedItem.id,
            count: nextCount,
          };

          await this.$store.dispatch('fileBrowserProductSelector/pickItem', pickedProductItem);

          return isUploadedProduct
            ? this.getPickedUploadProductByFileId(fileId) || pickedProductItem
            : this.getPickedInlineProductByFileId(fileId) || pickedProductItem;
        }

        await this.$store.dispatch(
          'fileBrowserProductSelector/setCurrentPickedItems',
          Array.from({ length: nextCount }, () => fileId)
        );

        const pickedItem = this.getPickedInlineProductByFileId(fileId);

        if (pickedItem && Number(pickedItem.count || 0) >= nextCount) {
          return pickedItem;
        }
      }

      this.removeInlineProductRecent(recentItem);
      toastr.error('Recent product no longer exists.');
    },
    getInlineProductRecentsScope() {
      const companyId = window.$?.('#company_selector')?.val?.()
        || window.companyId
        || window.company_id
        || 'default';

      return String(companyId || 'default');
    },
    getInlineProductRecentsStorageKey() {
      return `${INLINE_PRODUCT_RECENTS_STORAGE_KEY}:${this.getInlineProductRecentsScope()}`;
    },
    normalizeInlineProductRecentItem(item) {
      if (!item) {
        return null;
      }

      const id = item.id || item.fileId || item.path || item.full_url || item.name;

      if (!id) {
        return null;
      }

      return {
        id,
        fileId: item.fileId || item.id || '',
        name: item.name || item.product_name || item.title || item.fileName || 'Product',
        product_name: item.product_name || item.title || item.name || item.fileName || 'Product',
        title: item.title || item.product_name || item.name || item.fileName || 'Product',
        fileName: item.fileName || item.name || '',
        upc: item.upc || '',
        gtin: item.gtin || '',
        sku: item.sku || '',
        path: item.path || '',
        full_url: item.full_url,
        thumbnail: item.thumbnail,
        thumbnail_path: item.thumbnail_path,
        product_type: item.product_type || '',
        original_path: item.original_path || '',
        type: item.type || 'file',
        count: 0,
      };
    },
    isCatalogRecentProductItem(item) {
      if (!item) {
        return false;
      }

      const productType = String(item.product_type || '').trim();
      const fileId = String(item.fileId || '').trim();

      return !productType
        && !fileId.startsWith('salsify:')
        && !fileId.startsWith('asin:')
        && !fileId.startsWith('bynder:');
    },
    loadInlineProductRecents() {
      try {
        const stored = JSON.parse(
          window.localStorage.getItem(this.getInlineProductRecentsStorageKey()) || '[]'
        );

        if (!Array.isArray(stored)) {
          return [];
        }

        return stored
          .map((item) => this.normalizeInlineProductRecentItem(item))
          .filter((item) => this.isCatalogRecentProductItem(item))
          .filter(Boolean)
          .slice(0, INLINE_PRODUCT_RECENTS_LIMIT);
      } catch (error) {
        return [];
      }
    },
    saveInlineProductRecents(items = []) {
      try {
        window.localStorage.setItem(
          this.getInlineProductRecentsStorageKey(),
          JSON.stringify(items.slice(0, INLINE_PRODUCT_RECENTS_LIMIT))
        );
      } catch (error) {
        console.log(error);
      }
    },
    clearInlineProductRecents() {
      this.inlineProductRecentItems = [];
      this.saveInlineProductRecents([]);
    },
    removeInlineProductRecent(item) {
      const recentKey = String(this.productItemKey(item));
      const nextItems = this.inlineProductRecentItems.filter((storedItem) => {
        return String(this.productItemKey(storedItem)) !== recentKey;
      });

      this.inlineProductRecentItems = nextItems;
      this.saveInlineProductRecents(nextItems);
    },
    rememberInlineProductRecent(item) {
      const recentItem = this.normalizeInlineProductRecentItem(item);

      if (!recentItem || !this.isCatalogRecentProductItem(recentItem)) {
        return;
      }

      const recentKey = String(this.productItemKey(recentItem));
      const nextItems = [
        recentItem,
        ...this.inlineProductRecentItems.filter((storedItem) => {
          return String(this.productItemKey(storedItem)) !== recentKey;
        }),
      ].slice(0, INLINE_PRODUCT_RECENTS_LIMIT);

      this.inlineProductRecentItems = nextItems;
      this.saveInlineProductRecents(nextItems);
    },
    productItemName(item) {
      const name = item.product_name || item.title || item.name || item.fileName || 'Product';

      return String(name).replace(/\.[^/.]+$/, '');
    },
    productItemCode(item) {
      return item.upc || item.gtin || item.sku || item.fileId || String(item.name || '').replace(/\.[^/.]+$/, '');
    },
    productItemThumbnail(item) {
      const path = item.thumbnail_path || item.thumbnail || item.path || '';

      if (!path) {
        return '';
      }

      if (String(path).startsWith('/share?file=')) {
        return path;
      }

      if (
        item.product_type === DISPLAY_BUILDER_UPLOAD_PRODUCT_TYPE
        && /^https?:\/\//.test(String(path))
      ) {
        return path;
      }

      return `/share?file=${path}`;
    },
    clearAutoFillSelectedItems() {
      this.autoFillSelectedItems = [];
    },
    async isExternalSourceAvailable(source) {
      const { data } = await axios.post('/platforms/is-platform-available', { platform: source });

      return data.available;
    },
    async fetchItems(page = 1) {
      this.isLoading = true;

      try {
        const { data } = await axios.get('/salsify/retrieve-products', {
          params: {
            pageNumber: page,
            pageSize: this.pageSize,
            searchKey: this.searchKey,
          },
        });

        if (data?.status === 'error') {
          toastr.error(data.message || 'Failed to load items');
          return;
        }
        
        this.items = (data.items || [])
          .map((item) => this.normalizeSalsifyProductItem(item))
          .filter(Boolean);
        this.currentPage = data.current_page || page;
        this.totalRows = data.totalCount || 0;
      } catch (error) {
        console.error('Error fetching items:', error);
        toastr.error('Failed to load items');
      } finally {
        this.isLoading = false;
      }
    },
    isImageFile(file) {
      return file?.type?.startsWith('image/');
    },
    async onFileSelected(event) {
      const file = event.target.files[0];

      await this.handleImageFile(file);
      event.target.value = '';
    },
    async handleImageFile(file) {
      if (!file) {
        return;
      }

      if (!this.isImageFile(file)) {
        toastr.error('Please select an image file');
        return;
      }

      await this.addProductImage(file);
    },
    async addProductImage(file) {
      const { id: templateId, customer_id, preview_id } = window.template_data;
      const canvas = window.previews[0]?.canvas;

      if (templateId == null || customer_id == null || preview_id == null) {
        toastr.error('Template data is incomplete');
        return;
      }

      if (!file) {
        toastr.error('No file selected');
        return;
      }

      $('.loadingOverlay').fadeIn();
      $('.loadingOverlay-text').html('Uploading Product Image...');
      try {
        const formData = new FormData();

        formData.append('name', file.name);
        formData.append('company_id', '-1');
        formData.append('upload_images[]', file);
        formData.append('image_type', 'product_image');
        formData.append('folder_id', this.folderId);

        const response = await axios.post('/file/uploadimg/store', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        const filename = response.data?.filenames?.[0];
        if (!filename) {
            throw new Error('Product image upload failed: no filename returned');
        }

        await this.handleFolderContent(this.folderId);
        $('.loadingOverlay').fadeOut();
      } catch (err) {
        $('.loadingOverlay').fadeOut();
        console.error('addProductImage error:', err);
        toastr.error(err.message || 'Unexpected error while uploading product image');
      }
    },
    async handleProductsFolder() {
      const response = await axios.post(`/file/check_folder_name_exists`,{
        name: PRODUCTS_DEFAULT_FOLDER_NAME,
        parent_id: null,
        isTemplateBox: true,
        tab: 'products',
      });

      if (response.data.exists) {
        this.folderId = response.data.folderId;
        await this.handleFolderContent(this.folderId);
        return;
      } 

      const responseFolder = await axios.post('/file/create_folder_by_name', {
        name: PRODUCTS_DEFAULT_FOLDER_NAME,
        parent_id: null,
        tab: 'products',
      });
      this.folderId = responseFolder.data.id;
    },
    async handleFolderContent(folderId) {
      const { data } = await axios.get(`/file/file_list?folder_id=${folderId}&pageNumber=1&pageSize=25`);

      this.uploadedProducts = data?.items || [];
    },
    onDragEnter(event) {
      if (!this.hasImageFile(event)) return;
      this.dragDepth += 1;
      this.isDraggingOver = true;
    },

    onDragOver(event) {
      if (!this.hasImageFile(event)) return;
      event.dataTransfer.dropEffect = 'copy';
      this.isDraggingOver = true;
    },

    onDragLeave(event) {
      if (!this.dragDepth) return;
      this.dragDepth -= 1;
      this.isDraggingOver = this.dragDepth > 0;
    },

    async onDrop(event) {
      this.dragDepth = 0;
      this.isDraggingOver = false;

      const files = Array.from(event.dataTransfer?.files || []);
      const file = files
        .find((item) => this.isImageFile(item));

      if (files.length && !file) {
        toastr.error('Please drop an image file');
        return;
      }

      await this.handleImageFile(file);
    },
    hasImageFile(event) {
      const items = Array.from(event.dataTransfer?.items || []);

      if (items.length) {
        return items.some((item) => item.kind === 'file' && item.type.startsWith('image/'));
      }

      return Array.from(event.dataTransfer?.files || []).some((file) => this.isImageFile(file));
    },
    handleProductItemClick(item) {
      if (this.inlineProductPickerSaving) {
        return;
      }

      if (this.isAutoFillMode) {
        this.toggleAutoFillProduct(item);
        return;
      }

      this.selectInlineProduct(item);
    },
  },
};
</script>
<style lang="scss" scoped>
.sp-inline-picker {
  position: fixed;
  z-index: 2050;
  display: flex;
  flex-direction: column;
  max-height: 340px;
  margin: 0;
  padding: 8px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(16, 24, 40, .12);
}

.sp-inline-picker--auto-fill {
  max-height: 520px;
  padding: 9px;
  border-color: #d9e4ee;
}

.sp-inline-picker__auto-head {
  margin-bottom: 8px;
}

.sp-inline-picker__auto-title {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #0f172a;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.2;
}

.sp-inline-picker__auto-title svg {
  color: #2eb89a;
  flex: 0 0 auto;
}

.sp-inline-picker__auto-subtitle {
  margin-top: 4px;
  color: #64748b;
  font-size: 11.5px;
  line-height: 1.25;
}

.sp-inline-picker__source-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
  padding: 4px;
  border-radius: 5px;
  background: #f1f5f9;
}

.sp-inline-picker__source-tab {
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #64748b;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  padding: 0.25rem;
  flex: 1 1 0%;
}

.sp-inline-picker__source-tab.active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 1px 2px rgba(15, 23, 42, .08);
}

.sp-inline-picker__source-tab:disabled {
  opacity: .55;
  cursor: default;
}

.sp-inline-picker__search {
  display: flex;
  align-items: center;
  gap: 7px;
  height: 1.75rem;
  padding: 0 9px;
  border: 1px solid #84dcca;
  border-radius: 6px;
  color: #3b9483;
  background: #ffffff;
}

.sp-inline-picker__input {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: 0;
  outline: 0;
  font-size: 12.5px;
  color: var(--text);
  background: transparent;
}

.sp-inline-picker__input::placeholder {
  color: #93a4b2;
}

.sp-inline-picker__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-height: 120px;
  margin-top: 8px;
  overflow-y: auto;
}

.sp-inline-picker__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 6px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background .12s;
}

.sp-inline-picker__item:hover:not(:disabled),
.sp-inline-picker__item:focus-visible {
  background: #F1F5F9;
  outline: 0;
}

.sp-inline-picker__item:disabled {
  opacity: .65;
  cursor: wait;
}

.sp-inline-picker__item--selectable {
  cursor: pointer;
}

.sp-inline-picker__item--selectable:hover,
.sp-inline-picker__item--selectable.selected {
  background: #f0fdfa;
}

.sp-inline-picker__checkbox {
  width: 14px;
  height: 14px;
  flex: 0 0 14px;
  margin: 0;
}

.sp-inline-picker__section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 6px 6px;
}

.sp-inline-picker__section-title {
  padding: 10px 6px 6px;
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.sp-inline-picker__section-heading .sp-inline-picker__section-title {
  padding: 0;
}

.sp-inline-picker__clear-recents {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  text-transform: none;
}

.sp-inline-picker__clear-recents:hover:not(:disabled) {
  color: var(--text);
}

.sp-inline-picker__clear-recents:disabled {
  cursor: wait;
  opacity: .55;
}

.sp-inline-picker__section-title--all {
  margin-top: 8px;
  border-top: 1px solid var(--border);
}

.sp-inline-picker__thumb {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  border-radius: 5px;
  background: #F1F5F9;
  overflow: hidden;
}

.sp-inline-picker__thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.sp-inline-picker__thumb-placeholder {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid var(--text-light);
}

.sp-inline-picker__copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sp-inline-picker__name {
  overflow: hidden;
  color: var(--text);
  font-size: 12.5px;
  font-weight: 700;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sp-inline-picker__code {
  color: var(--text-light);
  font-size: 10.5px;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.sp-inline-picker__state {
  padding: 12px 6px 8px;
  color: var(--text-light);
  font-size: 12px;
  text-align: center;
}

.sp-inline-picker__state--error {
  color: var(--danger);
}

.sp-inline-picker__footer {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 4px;
  margin: 8px -8px -8px;
  padding: 8px;
  border-top: 1px solid var(--border);
  border-radius: 0 0 8px 8px;
  background: #ffffff;
}

.sp-inline-picker__pager-btn {
  flex: 0 0 auto;
  min-width: 28px;
  width: auto;
  height: 28px;
  padding: 0 7px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sp-inline-picker__pager-btn--page {
  min-width: 36px;
}

.sp-inline-picker__pager-btn--page.active {
  min-width: 40px;
  background: #007964;
  color: #ffffff;
}

.sp-inline-picker__pager-ellipsis {
  flex: 0 0 auto;
  min-width: 20px;
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
}

.sp-inline-picker__auto-footer {
  position: sticky;
  bottom: 0;
  margin: 8px -9px -9px;
  padding: 8px;
  border-top: 1px solid var(--border);
  border-radius: 0 0 8px 8px;
  background: #ffffff;
}

.sp-inline-picker__pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.sp-inline-picker__pager--auto {
  margin-bottom: 8px;
}

.sp-inline-picker__fill-mode {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 4px;
  border-radius: 5px;
  background: #f1f5f9;
}

.sp-inline-picker__fill-mode-btn {
  height: 27px;
  min-width: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.sp-inline-picker__fill-mode-btn.active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 1px 2px rgba(15, 23, 42, .08);
}

.sp-inline-picker__fill-mode-btn:disabled {
  cursor: wait;
  opacity: .65;
}

.sp-inline-picker__auto-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 9px;
}

.sp-inline-picker__selected-count {
  min-width: 0;
  color: #64748b;
  font-size: 11px;
  line-height: 1.2;
}

.sp-inline-picker__submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 98px;
  height: 32px;
  padding: 0 12px;
  border: 0;
  border-radius: 6px;
  background: #35bea5;
  color: #ffffff;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, .08);
  opacity: 0.95;
}

.sp-inline-picker__submit:hover:not(:disabled) {
  opacity: 1;
}

.sp-inline-picker__submit:disabled {
  cursor: default;
  opacity: .55;
}

.sp-inline-picker__pager-btn:hover:not(:disabled) {
  background: var(--teal-dim);
  color: var(--teal);
}

.sp-inline-picker__pager-btn:disabled {
  opacity: .45;
  cursor: default;
}

.sp-inline-picker__pager-ellipsis {
  min-width: 28px;
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}
.product-picker__file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
.custom-drag-drop-area {
  color: #64748b;
  font-size: 11px;
  border-color: #e2e8f0;
  border-style: dashed;
  border-width: 1px;
  border-radius: 4px;
  cursor: pointer;
}
.custom-drag-drop-area {
  color: #64748b;
  font-size: 11px;
  border: 1px dashed #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  transition:
    color 0.15s ease,
    border-color 0.15s ease,
    background-color 0.15s ease;
}

.custom-drag-drop-area--active {
  color: #007964;
  border-color: #007964;
  background-color: #f0fdfa;
}
.custom-drag-drop-area:hover {
  color: #007964;
  border-color: #007964;
  background-color: #f0fdfa;
}
</style>
